/**
 * Definición de esquemas de color alternativos para Quip
 */
package link.erpango.quip.theme

import androidx.compose.ui.graphics.Color

// Estructura base para facilitar el intercambio
interface QuipColorScheme {
    val colorPrimary: Color
    val colorSecondary: Color
    val colorOnPrimary: Color
    val colorBackground: Color
    val colorSurface: Color
    val colorText: Color
    val colorTextSecondary: Color
    val gradientStart: Color
    val gradientEnd: Color
    val colorError: Color
    val colorSuccess: Color
    val colorWarning: Color
    val white: Color
    val black: Color
}

object ColorSchemes {

    // 0. Original (Pastel Green)
    object Original : QuipColorScheme {
        override val colorPrimary = Color(0xFF88D8B0)  // Soft Pastel Green
        override val colorSecondary = Color(0xFFBEE6CE) // Minty Fresh
        override val colorOnPrimary = Color(0xFFFFFFFF)
        override val colorBackground = Color(0xFFFAFAFA) // Off-White / Cream
        override val colorSurface = Color(0xFFFFFFFF)
        override val colorText = Color(0xFF2F3E46)     // Deep Slate
        override val colorTextSecondary = Color(0xFF5F736F) // Grey Sage
        override val gradientStart = Color(0xFF88D8B0)
        override val gradientEnd = Color(0xFF2E8B57) // Richer Sea Green
        override val colorError = Color(0xFFE57373)    // Soft Red
        override val colorSuccess = Color(0xFF388E3C)
        override val colorWarning = Color(0xFFFFA000)
        override val white = Color(0xFFFFFFFF)
        override val black = Color(0xFF000000)
    }

    // 1. Classic Mechanic (Blue/Grey) - Professional, Trustworthy
    object ClassicMechanic : QuipColorScheme {
        override val colorPrimary = Color(0xFF1976D2)     // Classic Blue
        override val colorSecondary = Color(0xFFBBDEFB)   // Light Blue
        override val colorOnPrimary = Color(0xFFFFFFFF)
        override val colorBackground = Color(0xFFF5F5F5)  // Light Grey
        override val colorSurface = Color(0xFFFFFFFF)
        override val colorText = Color(0xFF212121)        // Graphite
        override val colorTextSecondary = Color(0xFF757575)
        override val gradientStart = Color(0xFF2196F3) // Blue 500
        override val gradientEnd = Color(0xFF1565C0)   // Blue 800
        override val colorError = Color(0xFFD32F2F)
        override val colorSuccess = Color(0xFF388E3C)
        override val colorWarning = Color(0xFFFFA000)
        override val white = Color(0xFFFFFFFF)
        override val black = Color(0xFF000000)
    }

    // 2. Warning Yellow (Industrial) - High Contrast, Construction
    object WarningYellow : QuipColorScheme {
        override val colorPrimary = Color(0xFFFFC107)     // Amber
        override val colorSecondary = Color(0xFFFFECB3)   // Pale Amber
        override val colorOnPrimary = Color(0xFF212121)   // Dark Text on Yellow
        override val colorBackground = Color(0xFFFAFAFA)
        override val colorSurface = Color(0xFFFFFFFF)
        override val colorText = Color(0xFF212121)
        override val colorTextSecondary = Color(0xFF616161)
        override val gradientStart = Color(0xFFFFB300) // Amber 600
        override val gradientEnd = Color(0xFFFF6F00)   // Amber 900
        override val colorError = Color(0xFFD32F2F)
        override val colorSuccess = Color(0xFF388E3C)
        override val colorWarning = Color(0xFF3E2723)  // Dark contrast
        override val white = Color(0xFFFFFFFF)
        override val black = Color(0xFF000000)
    }

    // 3. Racer Red (Sporty) - Energetic, Automotive
    object RacerRed : QuipColorScheme {
        override val colorPrimary = Color(0xFFD32F2F)     // Red
        override val colorSecondary = Color(0xFFFFCDD2)   // Light Red
        override val colorOnPrimary = Color(0xFFFFFFFF)
        override val colorBackground = Color(0xFFFFFFFF)
        override val colorSurface = Color(0xFFFAFAFA)
        override val colorText = Color(0xFF212121)
        override val colorTextSecondary = Color(0xFF757575)
        override val gradientStart = Color(0xFFF44336) // Red 500
        override val gradientEnd = Color(0xFF8E0000)   // Crimson
        override val colorError = Color(0xFFB00020)
        override val colorSuccess = Color(0xFF00C853)
        override val colorWarning = Color(0xFFFFC107)
        override val white = Color(0xFFFFFFFF)
        override val black = Color(0xFF000000)
    }

    // 4. Forest Green (Eco Drive) - Stronger natural green
    object ForestGreen : QuipColorScheme {
        override val colorPrimary = Color(0xFF2E7D32)     // Forest Green
        override val colorSecondary = Color(0xFFC8E6C9)   // Pale Green
        override val colorOnPrimary = Color(0xFFFFFFFF)
        override val colorBackground = Color(0xFFF1F8E9)  // Very Light Green Tint
        override val colorSurface = Color(0xFFFFFFFF)
        override val colorText = Color(0xFF1B5E20)        // Dark Green Text
        override val colorTextSecondary = Color(0xFF558B2F)
        override val gradientStart = Color(0xFF4CAF50) // Green 500
        override val gradientEnd = Color(0xFF003300)   // Deep Forest
        override val colorError = Color(0xFFD32F2F)
        override val colorSuccess = Color(0xFF43A047)
        override val colorWarning = Color(0xFFFF8F00)
        override val white = Color(0xFFFFFFFF)
        override val black = Color(0xFF000000)
    }

    // 5. Midnight Garage (Dark Mode - Blue)
    object MidnightGarage : QuipColorScheme {
        override val colorPrimary = Color(0xFF90CAF9)     // Light Blue Accent
        override val colorSecondary = Color(0xFF1E88E5)   // Stronger Blue
        override val colorOnPrimary = Color(0xFF0D47A1)   // Dark Text on Light Blue
        override val colorBackground = Color(0xFF121212)  // Almost Black
        override val colorSurface = Color(0xFF1E1E1E)
        override val colorText = Color(0xFFE0E0E0)        // Light Grey
        override val colorTextSecondary = Color(0xFFB0BEC5)
        override val gradientStart = Color(0xFF64B5F6) // Blue 300
        override val gradientEnd = Color(0xFF01579B)   // Light Blue 900
        override val colorError = Color(0xFFEF9A9A)
        override val colorSuccess = Color(0xFFA5D6A7)
        override val colorWarning = Color(0xFFFFE082)
        override val white = Color(0xFFFFFFFF)
        override val black = Color(0xFF000000)
    }

    // 6. Obsidian (Dark Mode - Mono)
    object Obsidian : QuipColorScheme {
        override val colorPrimary = Color(0xFFEEEEEE)     // Almost White
        override val colorSecondary = Color(0xFF424242)   // Charcoal
        override val colorOnPrimary = Color(0xFF000000)
        override val colorBackground = Color(0xFF000000)  // Pitch Black
        override val colorSurface = Color(0xFF121212)
        override val colorText = Color(0xFFFFFFFF)
        override val colorTextSecondary = Color(0xFF9E9E9E)
        override val gradientStart = Color(0xFFE0E0E0) // Silver
        override val gradientEnd = Color(0xFF616161)   // Dark Grey
        override val colorError = Color(0xFFEF9A9A)
        override val colorSuccess = Color(0xFFA5D6A7)
        override val colorWarning = Color(0xFFFFE082)
        override val white = Color(0xFFFFFFFF)
        override val black = Color(0xFF000000)
    }

    // 7. Luxury Sedan (Gold/Black) - Premium
    object LuxurySedan : QuipColorScheme {
        override val colorPrimary = Color(0xFFC5A059)     // Metallic Gold
        override val colorSecondary = Color(0xFF2C2C2C)   // Dark Grey
        override val colorOnPrimary = Color(0xFF000000)
        override val colorBackground = Color(0xFF1A1A1A)  // Dark Background
        override val colorSurface = Color(0xFF2C2C2C)
        override val colorText = Color(0xFFE0E0E0)
        override val colorTextSecondary = Color(0xFFBDBDBD)
        override val gradientStart = Color(0xFFD4AF37) // Metallic Gold
        override val gradientEnd = Color(0xFF8B6914)   // Dark Goldenrod
        override val colorError = Color(0xFFCF6679)
        override val colorSuccess = Color(0xFF81C784)
        override val colorWarning = Color(0xFFE6C200)
        override val white = Color(0xFFFFFFFF)
        override val black = Color(0xFF000000)
    }

    // 8. Dusty Vintage (Orange/Brown) - Distinctive
    object DustyVintage : QuipColorScheme {
        override val colorPrimary = Color(0xFFFF7043)     // Deep Orange
        override val colorSecondary = Color(0xFFFFCCBC)   // Light Orange
        override val colorOnPrimary = Color(0xFFFFFFFF)
        override val colorBackground = Color(0xFFFFF3E0)  // Peach/Paper
        override val colorSurface = Color(0xFFFFFFFF)
        override val colorText = Color(0xFF3E2723)        // Dark Brown
        override val colorTextSecondary = Color(0xFF795548)
        override val gradientStart = Color(0xFFFF5722) // Deep Orange 500
        override val gradientEnd = Color(0xFFBF360C)   // Deep Orange 900
        override val colorError = Color(0xFFD32F2F)
        override val colorSuccess = Color(0xFF388E3C)
        override val colorWarning = Color(0xFFFFA000)
        override val white = Color(0xFFFFFFFF)
        override val black = Color(0xFF000000)
    }

    // 9. Electric Future (Cyan/Purple) - Modern Tech
    object ElectricFuture : QuipColorScheme {
        override val colorPrimary = Color(0xFF00E5FF)     // Cyan
        override val colorSecondary = Color(0xFF6200EA)   // Deep Purple
        override val colorOnPrimary = Color(0xFF000000)
        override val colorBackground = Color(0xFF263238)  // Dark Blue Grey
        override val colorSurface = Color(0xFF37474F)
        override val colorText = Color(0xFFECEFF1)
        override val colorTextSecondary = Color(0xFFB0BEC5)
        override val gradientStart = Color(0xFF00E5FF) // Cyan Accent
        override val gradientEnd = Color(0xFF651FFF)   // Deep Purple Accent
        override val colorError = Color(0xFFFF5252)
        override val colorSuccess = Color(0xFF69F0AE)
        override val colorWarning = Color(0xFFFFD740)
        override val white = Color(0xFFFFFFFF)
        override val black = Color(0xFF000000)
    }

    // 10. Clean Slate (Minimalist White/Grey)
    object CleanSlate : QuipColorScheme {
        override val colorPrimary = Color(0xFF455A64)     // Blue Grey
        override val colorSecondary = Color(0xFFCFD8DC)   // Light Blue Grey
        override val colorOnPrimary = Color(0xFFFFFFFF)
        override val colorBackground = Color(0xFFFFFFFF)  // Pure White
        override val colorSurface = Color(0xFFF5F5F5)
        override val colorText = Color(0xFF263238)        // Dark Blue Grey
        override val colorTextSecondary = Color(0xFF78909C)
        override val gradientStart = Color(0xFF78909C) // Blue Grey 400
        override val gradientEnd = Color(0xFF37474F)   // Blue Grey 800
        override val colorError = Color(0xFFD32F2F)
        override val colorSuccess = Color(0xFF388E3C)
        override val colorWarning = Color(0xFFFFA000)
        override val white = Color(0xFFFFFFFF)
        override val black = Color(0xFF000000)
    }
    
    // 11. Neutral Light - Tema neutro claro, deja que brille el color del vehículo
    object NeutralLight : QuipColorScheme {
        override val colorPrimary = Color(0xFF607D8B)     // Blue Grey 500
        override val colorSecondary = Color(0xFFECEFF1)   // Blue Grey 50
        override val colorOnPrimary = Color(0xFFFFFFFF)
        override val colorBackground = Color(0xFFFAFAFA)  // Grey 50
        override val colorSurface = Color(0xFFFFFFFF)
        override val colorText = Color(0xFF37474F)        // Blue Grey 800
        override val colorTextSecondary = Color(0xFF78909C) // Blue Grey 400
        override val gradientStart = Color(0xFFECEFF1)  // Muy sutil - casi blanco
        override val gradientEnd = Color(0xFFCFD8DC)    // Blue Grey 100
        override val colorError = Color(0xFFE53935)
        override val colorSuccess = Color(0xFF43A047)
        override val colorWarning = Color(0xFFFB8C00)
        override val white = Color(0xFFFFFFFF)
        override val black = Color(0xFF000000)
    }
    
    // 12. Neutral Dark - Tema neutro oscuro, elegante y discreto
    object NeutralDark : QuipColorScheme {
        override val colorPrimary = Color(0xFF90A4AE)     // Blue Grey 300
        override val colorSecondary = Color(0xFF455A64)   // Blue Grey 700
        override val colorOnPrimary = Color(0xFF000000)
        override val colorBackground = Color(0xFF1C1C1E)  // iOS Dark Background
        override val colorSurface = Color(0xFF2C2C2E)
        override val colorText = Color(0xFFE5E5E5)        // Light Grey
        override val colorTextSecondary = Color(0xFF8E8E93) // iOS Secondary Grey
        override val gradientStart = Color(0xFF3A3A3C)  // iOS Elevated Surface
        override val gradientEnd = Color(0xFF2C2C2E)    // iOS Surface
        override val colorError = Color(0xFFFF6B6B)
        override val colorSuccess = Color(0xFF69F0AE)
        override val colorWarning = Color(0xFFFFB74D)
        override val white = Color(0xFFFFFFFF)
        override val black = Color(0xFF000000)
    }
    
    // 13. Pure White - Minimalista extremo, todo blanco con acentos grises
    object PureWhite : QuipColorScheme {
        override val colorPrimary = Color(0xFF424242)     // Grey 800
        override val colorSecondary = Color(0xFFF5F5F5)   // Grey 100
        override val colorOnPrimary = Color(0xFFFFFFFF)
        override val colorBackground = Color(0xFFFFFFFF)  // Pure White
        override val colorSurface = Color(0xFFFAFAFA)
        override val colorText = Color(0xFF212121)        // Grey 900
        override val colorTextSecondary = Color(0xFF757575) // Grey 600
        override val gradientStart = Color(0xFFF5F5F5)  // Grey 100
        override val gradientEnd = Color(0xFFFFFFFF)    // Pure White
        override val colorError = Color(0xFFD32F2F)
        override val colorSuccess = Color(0xFF388E3C)
        override val colorWarning = Color(0xFFFFA000)
        override val white = Color(0xFFFFFFFF)
        override val black = Color(0xFF000000)
    }
    
    // 14. Warm Neutral - Tonos cálidos y acogedores, neutros pero no fríos
    object WarmNeutral : QuipColorScheme {
        override val colorPrimary = Color(0xFF8D6E63)     // Brown 400
        override val colorSecondary = Color(0xFFEFEBE9)   // Brown 50
        override val colorOnPrimary = Color(0xFFFFFFFF)
        override val colorBackground = Color(0xFFFAF8F5)  // Warm White
        override val colorSurface = Color(0xFFFFFFFF)
        override val colorText = Color(0xFF3E2723)        // Brown 900
        override val colorTextSecondary = Color(0xFF6D4C41) // Brown 700
        override val gradientStart = Color(0xFFEFEBE9)  // Brown 50
        override val gradientEnd = Color(0xFFFAF8F5)    // Warm White
        override val colorError = Color(0xFFD32F2F)
        override val colorSuccess = Color(0xFF558B2F)
        override val colorWarning = Color(0xFFFF8F00)
        override val white = Color(0xFFFFFFFF)
        override val black = Color(0xFF000000)
    }
    
    // 15. Cool Neutral - Tonos fríos y modernos, estilo tech/startup
    object CoolNeutral : QuipColorScheme {
        override val colorPrimary = Color(0xFF546E7A)     // Blue Grey 600
        override val colorSecondary = Color(0xFFE1F5FE)   // Light Blue 50
        override val colorOnPrimary = Color(0xFFFFFFFF)
        override val colorBackground = Color(0xFFF8F9FA)  // Cool Grey
        override val colorSurface = Color(0xFFFFFFFF)
        override val colorText = Color(0xFF263238)        // Blue Grey 900
        override val colorTextSecondary = Color(0xFF607D8B) // Blue Grey 500
        override val gradientStart = Color(0xFFE8EEF1)  // Cool Light Grey
        override val gradientEnd = Color(0xFFF8F9FA)    // Cool Grey
        override val colorError = Color(0xFFE53935)
        override val colorSuccess = Color(0xFF00897B)
        override val colorWarning = Color(0xFFFDD835)
        override val white = Color(0xFFFFFFFF)
        override val black = Color(0xFF000000)
    }

    // 16. Logo Green - Verde del logo como protagonista, fondo crema
    object LogoGreen : QuipColorScheme {
        override val colorPrimary = Color(0xFF5BAD8F)     // Logo wrench green (midpoint)
        override val colorSecondary = Color(0xFFA8E6C4)   // Pale mint
        override val colorOnPrimary = Color(0xFFFFFFFF)
        override val colorBackground = Color(0xFFFFFBEA)  // Logo background cream
        override val colorSurface = Color(0xFFFFFFFF)
        override val colorText = Color(0xFF2F3E46)        // Deep Slate
        override val colorTextSecondary = Color(0xFF4A8870) // Muted green-grey
        override val gradientStart = Color(0xFF6DBFA0)   // Logo green light
        override val gradientEnd = Color(0xFF3A9E7A)     // Logo green dark
        override val colorError = Color(0xFFE57373)
        override val colorSuccess = Color(0xFF388E3C)
        override val colorWarning = Color(0xFFF5C800)    // Logo yellow as warning accent
        override val white = Color(0xFFFFFFFF)
        override val black = Color(0xFF000000)
    }

    // 17. Logo Yellow - Amarillo del logo como protagonista, acentos verdes
    object LogoYellow : QuipColorScheme {
        override val colorPrimary = Color(0xFFF5C800)     // Logo yellow (border/spine)
        override val colorSecondary = Color(0xFF6DBFA0)   // Logo green as secondary
        override val colorOnPrimary = Color(0xFF2F3E46)   // Dark text on yellow
        override val colorBackground = Color(0xFFFFFBEA)  // Logo background cream
        override val colorSurface = Color(0xFFFFFFFF)
        override val colorText = Color(0xFF2F3E46)        // Deep Slate
        override val colorTextSecondary = Color(0xFF7A6A2F) // Warm olive-grey
        override val gradientStart = Color(0xFFFFE066)   // Logo yellow light
        override val gradientEnd = Color(0xFFF5C800)     // Logo yellow dark
        override val colorError = Color(0xFFE57373)
        override val colorSuccess = Color(0xFF5BAD8F)    // Logo green as success
        override val colorWarning = Color(0xFFFF8F00)
        override val white = Color(0xFFFFFFFF)
        override val black = Color(0xFF000000)
    }

    val allSchemes = mapOf(
        "Original" to Original,
        "Classic Mechanic" to ClassicMechanic,
        "Warning Yellow" to WarningYellow,
        "Racer Red" to RacerRed,
        "Forest Green" to ForestGreen,
        "Midnight Garage" to MidnightGarage,
        "Obsidian" to Obsidian,
        "Luxury Sedan" to LuxurySedan,
        "Dusty Vintage" to DustyVintage,
        "Electric Future" to ElectricFuture,
        "Clean Slate" to CleanSlate,
        "Neutral Light" to NeutralLight,
        "Neutral Dark" to NeutralDark,
        "Pure White" to PureWhite,
        "Warm Neutral" to WarmNeutral,
        "Cool Neutral" to CoolNeutral,
        "Logo Green" to LogoGreen,
        "Logo Yellow" to LogoYellow
    )
}
